import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import * as Constants from '../../../misc/Constants'
import { COMMON_ERRORS } from '../../../misc/Constants'
import { archivePackage, deletePackage, getCommunitySettingsPackages } from '../../../actions/packagesActions'
import { reset, setArchived, setPage } from '../../../reducers/community-settings/pricingSettingsReducer'
import EditDisabledModal from './modals/EditDisabledModal'
import ArchivePackageConfirmationModal from './modals/ArchivePackageConfirmationModal'
import PackageFormContainer from './PackageFormContainer'
import PackagesTable from './PackagesTable'
import StripeConnection from './StripeConnection'

import './PricingTab.scss'
import { ReactComponent as RoundedPlusIcon } from '../../../assets/images/common/icons/rounded-plus-icon.svg'
import DeletePackageConfirmationModal from './modals/DeletePackageConfirmationModal'

const PricingTab = () => {
  const dispatch = useDispatch()

  const { packages, archived, selectedPackage, totalPages } = useSelector(state => state.communitySettings.pricing)

  const [showPackageFormModal, setShowPackageFormModal] = useState(false)
  const [showEditDisabledModal, setShowEditDisabledModal] = useState(false)
  const [showArchiveConfirmationModal, setShowArchiveConfirmationModal] = useState(false)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)
  const [confirmationPackage, setConfirmationPackage] = useState(null)

  const fetchPackages = (newPage = 1) => {
    if (totalPages && newPage === totalPages && packages.length === 1) {
      newPage -= 1
    }
    dispatch(setPage(newPage))
    dispatch(getCommunitySettingsPackages({ archived, page: newPage, perPage: Constants.PER_PAGE_DEFAULT, mode: 'stats' }))
  }

  useEffect(() => () => {
    dispatch(setPage(1))
    dispatch(reset())
  }, [dispatch])

  useEffect(() => {
    dispatch(getCommunitySettingsPackages({ archived, page: 1, perPage: Constants.PER_PAGE_DEFAULT, mode: 'stats' }))
  }, [archived, dispatch])

  useEffect(() => {
    if (selectedPackage) {
      setShowPackageFormModal(true)
    }
  }, [selectedPackage])

  const toggleArchiveConfirmationModal = (pkg) => {
    setShowArchiveConfirmationModal(true)
    setConfirmationPackage(pkg)
  }

  const closeArchiveConfirmationModal = () => {
    setShowArchiveConfirmationModal(false)
    setConfirmationPackage(null)
  }

  const handleArchivePackage = () => {
    dispatch(archivePackage(confirmationPackage.id))
      .unwrap()
      .then(() => {
        closeArchiveConfirmationModal()
        toast.success(`"${confirmationPackage.name}" was archived`, { position: toast.POSITION.TOP_RIGHT })
      })
      .catch(() => toast.error(COMMON_ERRORS['500'], { position: toast.POSITION.TOP_RIGHT }))
  }

  const toggleDeleteConfirmationModal = (pkg) => {
    setShowDeleteConfirmationModal(true)
    setConfirmationPackage(pkg)
  }

  const closeDeleteConfirmationModal = () => {
    setShowDeleteConfirmationModal(false)
    setConfirmationPackage(null)
  }

  const handleDeletePackage = () => {
    dispatch(deletePackage(confirmationPackage))
      .unwrap()
      .then(() => {
        closeDeleteConfirmationModal()
        toast.success(`"${confirmationPackage.name}" was deleted`, { position: toast.POSITION.TOP_RIGHT })
      })
  }

  const toggleEditDisabledModal = () => {
    setShowEditDisabledModal(true)
  }

  const closeEditDisabledModal = () => {
    setShowEditDisabledModal(false)
  }

  const toggleArchivedValue = (e) => {
    const newValue = e.target.value === 'true'
    if (archived !== newValue) {
      dispatch(setArchived(newValue))
    }
  }

  return (
    <div id='PricingTab'>
      <StripeConnection />
      <div className='d-flex community-settings-main-row'>
        <div className='d-flex flex-column community-pricing-packages'>
          <p className='community-pricing-packages-title'>Community Pricing Packages</p>
          <p className='community-pricing-packages-description'>Use packages to offer different pricing options for your classes. Students can see
            and select available packages when they register for a class.</p>
        </div>

        <div className='d-flex justify-content-center align-items-center packages-filter-buttons'>
          <button
            className={`packages-filter-button ${archived ? 'packages-filter-unselected' : 'packages-filter-selected'}`}
            value='false'
            onClick={toggleArchivedValue}
          >
            Available
          </button>
          <button
            className={`packages-filter-button ${archived ? 'packages-filter-selected' : 'packages-filter-unselected'}`}
            value='true'
            onClick={toggleArchivedValue}
          >
            Archived
          </button>
        </div>

        <div className='package-add-wrapper'>
          <button
            type='button'
            className='d-flex justify-content-center align-items-center package-add-button'
            onClick={() => setShowPackageFormModal(true)}
          >
            <RoundedPlusIcon id='roundedPlusIcon' /> Create new package
          </button>
        </div>
      </div>

      <div className='table-wrapper'>
        <PackagesTable
          fetchPackages={fetchPackages}
          toggleArchiveConfirmationModal={toggleArchiveConfirmationModal}
          toggleEditDisabledModal={toggleEditDisabledModal}
          toggleDeleteConfirmationModal={toggleDeleteConfirmationModal}
          showPopover={!showEditDisabledModal && !showArchiveConfirmationModal && !showPackageFormModal}
        />
      </div>
      <EditDisabledModal
        showModal={showEditDisabledModal}
        closeModal={() => closeEditDisabledModal()}
      />
      <ArchivePackageConfirmationModal
        showModal={showArchiveConfirmationModal}
        closeModal={() => closeArchiveConfirmationModal()}
        onButtonClick={handleArchivePackage}
        isSingleWithinProgram={confirmationPackage?.isSingleWithinCourse}
      />
      <DeletePackageConfirmationModal
        showModal={showDeleteConfirmationModal}
        closeModal={() => closeDeleteConfirmationModal()}
        onButtonClick={handleDeletePackage}
        isSingleWithinProgram={confirmationPackage?.isSingleWithinCourse}
      />
      <PackageFormContainer
        showPackageFormModal={showPackageFormModal}
        setShowPackageFormModal={setShowPackageFormModal}
      />
    </div>
  )
}

export default PricingTab
